import { checkSearchTemplates, updateSearchTemplate } from '@/apis/searchtemplates';
import { queryClient } from '@/App';
import { SEARCH_TEMPLATE_DESCRIPTION_MAX_LEN, SEARCH_TEMPLATE_NAME_MAX_LEN } from '@/constants';
import { ApiBodyError, isDefinedError } from '@/errors';
import { AgDialog, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { TextField } from '@mui/material';
import { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';

const SEARCH_TEMPLATE_DUPLICATE_MESSAGE = 'Template name is already in use.';

export type EditTemplateDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  searchTemplateId: number;
  editSearchTemplateName: string;
  setEditSearchTemplateName: Dispatch<SetStateAction<string>>;
  editSearchTemplateDescription: string;
  setEditSearchTemplateDescription: Dispatch<SetStateAction<string>>;
  originalSearchTemplateName: string;
};

export const EditTemplateDialog = (props: EditTemplateDialogProps) => {
  const {
    isOpen,
    onClose,
    searchTemplateId,
    editSearchTemplateName,
    setEditSearchTemplateName,
    editSearchTemplateDescription,
    setEditSearchTemplateDescription,
    originalSearchTemplateName,
  } = props;
  const { enqueueSuccessNotification } = useSaladBar();
  const { getAccessTokenSilently } = useAuth0();

  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isSaveTemplateError, setIsSaveTemplateError] = useState(false);
  const [saveTemplateErrorText, setSaveTemplateErrorText] = useState('');
  const [isDuplicateDetected, setIsDuplicateDetected] = useState(false);

  const clearStateVariables = () => {
    setIsSavingTemplate(false);
    setIsSaveTemplateError(false);
    setSaveTemplateErrorText('');
    setIsDuplicateDetected(false);
  };

  const handleEditSearchTemplateNameChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    setEditSearchTemplateName(event.target.value);
  };

  const handleEditSearchTemplateDescriptionChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    setEditSearchTemplateDescription(event.target.value);
  };

  const handleEditSearchTemplate = async () => {
    try {
      setIsSavingTemplate(true);
      await updateSearchTemplate(await getAccessTokenSilently(), searchTemplateId, {
        name: editSearchTemplateName,
        description: editSearchTemplateDescription,
      });
      clearStateVariables();
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['searchtemplates'] });
      enqueueSuccessNotification(`Search template ${editSearchTemplateName} successfully updated`);
    } catch (e) {
      setIsSavingTemplate(false);
      setIsSaveTemplateError(true);
      if (
        e instanceof ApiBodyError &&
        isDefinedError(e) &&
        e.body.errors[0]?.name === 'UpdateSearchTemplateDuplicateNameError'
      ) {
        setSaveTemplateErrorText(SEARCH_TEMPLATE_DUPLICATE_MESSAGE);
      } else {
        setSaveTemplateErrorText('There was an issue with the backend.');
      }
    }
  };

  const checkNameClashOnBlurOrEnter = async () => {
    if (!editSearchTemplateName) return;
    if (editSearchTemplateName === originalSearchTemplateName) return;
    const token = await getAccessTokenSilently();
    const checkSearchResult = await checkSearchTemplates(token, { name: editSearchTemplateName });
    setIsDuplicateDetected(checkSearchResult);
  };

  const handleNameBlur = async () => {
    await checkNameClashOnBlurOrEnter();
  };

  const handleNameKeyPress = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      await checkNameClashOnBlurOrEnter();
    }
  };

  return (
    <AgDialog
      isOpen={isOpen}
      onClose={() => {
        clearStateVariables();
        onClose();
      }}
      dialogTitle="Edit search template"
      primaryButton={{
        text: 'Save',
        onClick: async () => {
          await handleEditSearchTemplate();
        },
        disabled:
          editSearchTemplateName.length < 3 ||
          editSearchTemplateDescription.length < 3 ||
          isSavingTemplate ||
          isDuplicateDetected,
      }}
      secondaryButton={{
        text: 'Cancel',
        onClick: async () => {
          clearStateVariables();
          onClose();
        },
        disabled: isSavingTemplate,
      }}
      data-testid="qadCreateSearchTemplateDialog"
    >
      <TextField
        margin="dense"
        id="name"
        label={'Template name'}
        type="text"
        fullWidth
        variant="standard"
        value={editSearchTemplateName}
        onChange={handleEditSearchTemplateNameChange}
        slotProps={{
          htmlInput: {
            maxLength: SEARCH_TEMPLATE_NAME_MAX_LEN,
          },
        }}
        error={isDuplicateDetected ? true : isSaveTemplateError}
        helperText={isDuplicateDetected ? SEARCH_TEMPLATE_DUPLICATE_MESSAGE : saveTemplateErrorText}
        onBlur={handleNameBlur}
        onKeyUp={handleNameKeyPress}
      />
      <TextField
        margin="dense"
        id="description"
        label={'Description'}
        type="text"
        fullWidth
        variant="filled"
        multiline
        value={editSearchTemplateDescription}
        onChange={handleEditSearchTemplateDescriptionChange}
        minRows={2}
        slotProps={{
          htmlInput: {
            maxLength: SEARCH_TEMPLATE_DESCRIPTION_MAX_LEN,
          },
        }}
      />
    </AgDialog>
  );
};
